import { msalInstance } from "@/plugins/msal";
import { getUserStats } from "@/services/subscription.service";
import { logout } from "@/services/auth.service";

const state = {
    user: null,
    accessToken: null,
    stats: null,
    error: null,
    isLoading: false
};

const mutations = {
    SET_USER(state, user) {
        state.user = user;
    },
    SET_ACCESS_TOKEN(state, token) {
        state.accessToken = token;
    },
    SET_STATS(state, stats) {
        state.stats = stats;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    }
};

const actions = {
    async populateUserDetails({ commit, dispatch }) {
        commit('SET_LOADING', true);
        try {
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length === 0) {
                console.log("No accounts found - user not authenticated");
                return;
            }

            const account = accounts[0];
            commit("SET_USER", account);

            const stats = await getUserStats();
            commit("SET_STATS", stats);
            
            // Update upload queue limit based on user stats
            dispatch('files/updateUploadQueueLimit', null, { root: true });

        } catch (error) {
            console.error("Error fetching user details:", error);
            commit('SET_ERROR', error.message || 'Failed to fetch user details');
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async logoutAction({ commit, dispatch, rootState }) {
        try {
            console.log('🔄 Starting comprehensive logout process...');
            
            // First, abort any ongoing file operations
            console.log('📁 Aborting any ongoing file operations...');
            const isFileOperationInProgress = rootState.files.isLoading.upload || rootState.files.isLoading.download;
            if (isFileOperationInProgress) {
                const isUploadInProgress = rootState.files.isLoading.upload;
                const isDownloadInProgress = rootState.files.isLoading.download;
                
                if (isUploadInProgress) {
                    await dispatch('files/abortUploadAction', null, { root: true });
                } else if (isDownloadInProgress) {
                    await dispatch('files/abortDownloadAction', null, { root: true });
                }
            }
            
            // Clear encryption state first
            console.log('🔐 Clearing encryption state...');
            await dispatch('encryption/clearEncryption', null, { root: true });
            console.log('✅ Encryption state cleared');
            
            // Clear all Vuex store modules
            console.log('🗂️ Clearing all store modules...');
            
            // Clear user state
            commit("SET_USER", null);
            commit("SET_ACCESS_TOKEN", null);
            commit("SET_STATS", null);
            commit("SET_ERROR", null);
            
            // Clear files state using the dedicated clear action
            await dispatch('files/clearFilesState', null, { root: true });
            
            // Clear contacts state using the dedicated clear action
            await dispatch('contacts/clearContactsState', null, { root: true });
            
            // Clear snackbar state
            dispatch('snackbar/hideSnackbar', null, { root: true });
            
            // Clear subscription state using the dedicated clear action
            await dispatch('subscription/clearSubscriptionState', null, { root: true });
            
            console.log('✅ All store modules cleared');
            
            // Clear all sessionStorage and localStorage
            console.log('💾 Clearing all storage...');
            sessionStorage.clear();
            localStorage.clear();
            console.log('✅ Storage cleared');
            
            // Clear MSAL cache and logout
            console.log('🔑 Clearing MSAL cache and logging out...');
            await logout();
            console.log('✅ MSAL logout completed');
            
            // Disable navigation prevention if active
            console.log('🚫 Disabling navigation prevention...');
            const { disableNavigationPrevention } = await import('@/utils/navigationPrevention');
            disableNavigationPrevention();
            console.log('✅ Navigation prevention disabled');
            
            console.log('✅ Comprehensive logout completed successfully');
            
        } catch (error) {
            console.error("❌ Logout failed:", error);
            commit('SET_ERROR', error.message || 'Failed to logout');
        } finally {
            commit('SET_LOADING', false);
        }
    }
};

const getters = {
    isAuthenticated: (state) => !!state.user,
    getUser: (state) => state.user,
    getAccessToken: (state) => state.accessToken,
    getStats: (state) => state.stats
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
